import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, Loader2, ArrowLeft, Mail, Info, ChevronRight } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      navigate('/reset-password', { state: { email } });
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D1B69] to-[#1A0F3D] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Logo and Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="flex justify-center">
          {logoError ? (
            <div className="inline-flex items-center justify-center w-[60px] h-[60px] bg-niwt-primary rounded-2xl">
              <span className="text-white text-2xl font-bold font-libre">N</span>
            </div>
          ) : (
            <img
              src="/images/niwt-logo.png"
              alt="Niwt"
              width={60}
              height={60}
              onError={() => setLogoError(true)}
            />
          )}
        </div>
        <h1 className="mt-6 text-center text-3xl font-libre font-bold text-white">Reset Your Password</h1>
        <p className="mt-2 text-center text-sm text-white/70">
          Enter your email and we'll send you a verification code
        </p>
      </div>

      {/* Form */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to login
          </Link>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all"
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#6B51A0] focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  Send Reset Code
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </form>

          {/* Security Note */}
          <div className="mt-6 p-3 bg-blue-50 rounded-lg flex items-start">
            <Info className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800">
              We'll send a 6-digit verification code to your email. If you don't receive it, check your spam folder.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center z-10">
        <div className="flex items-center justify-center gap-4 text-sm">
          <a href="https://www.niwt.com" className="text-white/80 hover:text-white hover:underline transition-colors">niwt.com</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/privacy" className="text-white/80 hover:text-white hover:underline transition-colors">Privacy</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/terms" className="text-white/80 hover:text-white hover:underline transition-colors">Terms</a>
        </div>
        <div className="mt-4 flex items-center justify-center gap-3">
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">SOC2</span>
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">ENCRYPTED</span>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
